import { ColumnFiltersState } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import { parseAsString, useQueryState } from 'nuqs';

import { DrawerLayout } from '@/components/layouts/drawer-layout';
import { ShipmentShare } from '@/components/modules/shipment/share';
import { DEFAULT_SHIPMENTS_TAB } from '@/components/modules/shipment/tabs';
import { Drawer } from '@/components/ui/drawer';
import { LoadingSpinner } from '@/components/ui/loading/spinner';
import { ShipmentList } from '@/components/views/shipment/list';
import { useDebounce } from '@/hooks/use-debounce';
import { useShipmentCollection } from '@/hooks/use-shipment-collection';
import { useShipmentDetails } from '@/hooks/use-shipment-details';
import { useShipmentFilters } from '@/hooks/use-shipment-filters';
import { useShipments } from '@/hooks/use-shipments';
import { DEFAULT_NUQS_CONFIG, DEFAULT_PAGINATION, SHARED_ITEM_TYPE, SHIPMENT_GROUPING } from '@/lib/constants';
import { ShipmentGrouping, ShipmentModel } from '@/lib/models/shipment/types';
import { TableLayout } from '@/types';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { parseAsCollectionFilter } from '@/utils/nuqs/parse-as-collection-filter';
import { parseAsColumnFilters } from '@/utils/nuqs/parse-as-column-filters';
import { parseAsPagination } from '@/utils/nuqs/parse-as-pagination';
import { parseAsShipmentGrouping } from '@/utils/nuqs/parse-as-shipment-grouping';
import { parseAsSorting } from '@/utils/nuqs/parse-as-sorting';

import { ShipmentDetailViewController } from '../detail';

const ShipmentListViewController = () => {
  const [selectedShipmentId, setSelectedShipmentId] = useQueryState(
    QUERY_PARAM.ID,
    parseAsString.withOptions(DEFAULT_NUQS_CONFIG),
  );

  const [pagination, setPagination] = useQueryState(
    QUERY_PARAM.PAGE,
    parseAsPagination.withOptions(DEFAULT_NUQS_CONFIG).withDefault(DEFAULT_PAGINATION),
  );

  const [sorting, setSorting] = useQueryState(
    QUERY_PARAM.SORTING,
    parseAsSorting.withOptions(DEFAULT_NUQS_CONFIG).withDefault([]),
  );

  const [columnFilters, setColumnFilters] = useQueryState(
    QUERY_PARAM.FILTERS,
    parseAsColumnFilters.withOptions(DEFAULT_NUQS_CONFIG).withDefault([]),
  );

  const [selectedTab, setSelectedTab] = useQueryState(
    QUERY_PARAM.TAB,
    parseAsString.withOptions(DEFAULT_NUQS_CONFIG).withDefault(DEFAULT_SHIPMENTS_TAB),
  );

  const [layout, setLayout] = useQueryState(
    QUERY_PARAM.LAYOUT,
    parseAsString.withOptions(DEFAULT_NUQS_CONFIG).withDefault(''),
  );

  const [grouping, setGrouping] = useQueryState(
    QUERY_PARAM.GROUPING,
    parseAsShipmentGrouping.withOptions(DEFAULT_NUQS_CONFIG).withDefault(SHIPMENT_GROUPING.MBOL),
  );

  const [collectionFilter, setCollectionFilter] = useQueryState(
    QUERY_PARAM.COLLECTION,
    parseAsCollectionFilter.withOptions(DEFAULT_NUQS_CONFIG),
  );

  const debouncedColumnFilters = useDebounce(columnFilters, 250);
  const { shipmentFilters } = useShipmentFilters({});
  const { shipments, error, isLoading, count, hasSearched, hasAppliedFilters, setShipmentTags } = useShipments({
    pagination,
    sorting,
    columnFilters: debouncedColumnFilters,
    grouping,
  });

  const {
    error: shipmentDetailError,
    isLoading: isShipmentDetailLoading,
    isRefreshing: isShipmentDetailRefreshing,
    forceDataRefresh: forceShipmentRefresh,
    shipment: shipmentDetail,
  } = useShipmentDetails({
    shipmentId: selectedShipmentId,
  });

  const {
    shipment: shipmentCollection,
    isLoading: isShipmentCollectionLoading,
    error: shipmentCollectionError,
  } = useShipmentCollection({ collectionFilter });

  const handleClickDetails = (shipment: ShipmentModel) => {
    if (shipment.associatedContainers?.length) {
      return setCollectionFilter({ type: 'mblNumber', value: shipment.mblNumber });
    }
    if (shipment.id) {
      return setSelectedShipmentId(shipment.id);
    }
    return undefined;
  };

  const onDetailsDrawerClose = () => {
    setSelectedShipmentId(null);
  };

  const onCollectionDrawerClose = () => {
    setCollectionFilter(null);
  };

  const handleChangeFilters = (newValue: ColumnFiltersState) => {
    setColumnFilters(newValue);

    // Reset pagination state when filters change
    if (!isEqual(pagination, DEFAULT_PAGINATION)) {
      setPagination(DEFAULT_PAGINATION);
    }
  };

  return (
    <>
      {hasSearched || Boolean(shipments) || error ? (
        <ShipmentList
          shipments={shipments || []}
          onClickDetails={handleClickDetails}
          error={error}
          isLoading={isLoading}
          onPaginationChange={setPagination}
          pagination={pagination}
          pageCount={count ? Math.ceil(count / (pagination.pageSize || 1)) : 0}
          onSortingChange={setSorting}
          sorting={sorting || undefined}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleChangeFilters}
          filterData={shipmentFilters}
          hasAppliedFilters={hasAppliedFilters}
          onUpdateShipmentTags={setShipmentTags}
          selectedTab={selectedTab}
          onSelectedTabChange={setSelectedTab}
          layout={layout as TableLayout}
          onLayoutChange={setLayout}
          grouping={grouping as ShipmentGrouping}
          onGroupingChange={(value) => {
            setGrouping(value);
          }}
        />
      ) : (
        <LoadingSpinner isVisible />
      )}
      <Drawer closeDrawer={onDetailsDrawerClose} isOpen={!!selectedShipmentId}>
        <DrawerLayout
          breadcrumbItems={[
            {
              label: `Back to ${collectionFilter ? 'collection' : 'shipments'}`,
              onClick: onDetailsDrawerClose,
            },
          ]}
          breadcrumbActions={<ShipmentShare shipmentId={selectedShipmentId || ''} type={SHARED_ITEM_TYPE.SHIPMENT} />}
        >
          {selectedShipmentId ? (
            <div className="max-w-screen-xl p-4">
              <ShipmentDetailViewController
                shipment={shipmentDetail}
                isLoading={isShipmentDetailLoading}
                isRefreshing={isShipmentDetailRefreshing}
                error={shipmentDetailError}
                onBack={onDetailsDrawerClose}
                onRequestRefresh={forceShipmentRefresh}
              />
            </div>
          ) : null}
        </DrawerLayout>
      </Drawer>
      <Drawer closeDrawer={onCollectionDrawerClose} isOpen={!!collectionFilter}>
        <DrawerLayout
          breadcrumbItems={[
            {
              label: 'Back to shipments',
              onClick: onCollectionDrawerClose,
            },
          ]}
          breadcrumbActions={
            <ShipmentShare shipmentId={collectionFilter?.value || ''} type={SHARED_ITEM_TYPE.COLLECTION} />
          }
        >
          {shipmentCollection ? (
            <div className="max-w-screen-xl p-4">
              <ShipmentDetailViewController
                shipment={shipmentCollection}
                isLoading={isShipmentCollectionLoading}
                error={shipmentCollectionError}
                onBack={onCollectionDrawerClose}
                onRequestRefresh={forceShipmentRefresh}
                onClickDetails={handleClickDetails}
              />
            </div>
          ) : null}
        </DrawerLayout>
      </Drawer>
    </>
  );
};

export { ShipmentListViewController };
